// library imports
import { useEffect, useRef } from "react";
import { Chart } from "@antv/g2";
import { uniqueId } from "@antv/util";
import { theme } from "antd";
import { isEqual } from "lodash";

const DEFAULT_ADDITIONAL_LINES_CONFIG = [];
const DEFAULT_ADDITIONAL_LINES_DATA = {};

// All the data must have same x arranged in same sequence, otherwise the chart will be messed up.
//
// additionalLinesConfig  — defined in frontend, stable (triggers full re-render on change)
//   [{ xAxis?, yAxis?, colorKey?, color? }, ...]
//
// additionalLinesData    — fetched from backend, updates via changeData (no full re-render)
//   [[...items], [...items], ...]   (indexed to match additionalLinesConfig)
export default function CustomLineBarChart({
  dataLine = [],
  dataBar = [],
  xAxisLine = "date",
  yAxisLine = "value",
  xAxisBar = "date",
  yAxisBar = "value",
  xAxisTitleLine = "Date",
  yAxisTitleLine = "Value",
  xAxisTitleBar = "Date",
  yAxisTitleBar = "Value",
  lineColor = "type",
  barColor = "type",
  barChartDomain = [],
  barChartRange = [],
  lineChartColor = [],
  additionalLinesConfig = DEFAULT_ADDITIONAL_LINES_CONFIG, // [{ key, xAxis?, yAxis?, colorKey?, color? }]
  additionalLinesData = DEFAULT_ADDITIONAL_LINES_DATA, // { [key]: [...] } — keyed by config entry key
}) {
  const { token } = theme.useToken();
  const containerRef = useRef(uniqueId("linebar-"));
  const container = containerRef.current;
  const chartRef = useRef(null);

  // Track previous data to avoid unnecessary updates
  const prevDataLineRef = useRef([]);
  const prevDataBarRef = useRef([]);
  const prevAdditionalLinesDataRef = useRef([]);
  // Keep a live ref to additionalLinesData so the tooltip closure stays current
  const additionalLinesDataRef = useRef(additionalLinesData);

  useEffect(() => {
    chartRef.current?.chart?.clear();

    const chartContainer = document.getElementById(container);
    chartContainer.innerHTML = "";

    const chart = new Chart({
      container: container,
      padding: "auto",
      marginBottom: 32,
      autoFit: true,
    });

    chart.scale("color", {
      domain: barChartDomain,
      range: barChartRange,
    });

    const bar = chart
      .interval()
      .data(dataBar)
      .encode("x", xAxisBar)
      .encode("y", yAxisBar)
      .encode("color", barColor)
      .transform({ type: "stackY" })
      .interaction("elementHighlight", { background: true });

    chart.axis("x", {
      line: true,
      lineStroke: token.colorTextBase,
      tickStroke: token.colorTextBase,
      title: xAxisTitleBar,
      titleFill: token.colorTextBase,
      labelFill: token.colorTextBase,
    });

    chart.axis("y", {
      line: true,
      lineStroke: token.colorTextBase,
      tickStroke: token.colorTextBase,
      title: yAxisTitleBar,
      titleFill: token.colorTextBase,
      labelFill: token.colorTextBase,
    });

    chart.legend({
      color: {
        itemLabelFill: token.colorTextBase,
      },
    });

    // config is stable (captured in closure); data is read from ref so it stays current
    const colorToLabel = () => {
      const map = {};
      additionalLinesConfig.forEach((cfg) => {
        const { key, color, colorKey = "type" } = cfg;
        const data = additionalLinesDataRef.current[key] ?? [];
        if (color && data.length > 0) {
          map[color] = data[0]?.[colorKey] ?? "";
        }
      });
      return map;
    };

    chart.interaction("tooltip", {
      render: (event, { title, items }) => {
        const normalizedTitle = Array.isArray(title) ? title[0] : title;
        return `<div style="width:150px;">
        <h3 style="padding:0;margin:0">${normalizedTitle}</h3>
        <ul style="padding: 0; margin: 0; list-style: none;">
          ${items
            .map((d) => {
              const name = colorToLabel()[d.color] ?? d.name;
              // Format the value with commas and 2 decimal places
              const formattedValue = d.value
                ? Number(d.value).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : d.value === 0
                  ? Number(d.value).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : d.value;
              return `
                <li style="display: flex; justify-content: space-between; align-items: center; margin: 4px 0;">
                  <span style="display: inline-flex; align-items: center;">
                    <span style="width: 8px; height: 8px; background-color: ${d.color}; border-radius: 50%; margin-right: 8px;"></span>
                    <span>${name}</span>
                  </span>
                  <span>${formattedValue}</span>
                </li>`;
            })
            .join("")}
        </ul>
      </div>`;
      },
    });

    const line = chart
      .line()
      .data(dataLine)
      .encode("x", xAxisLine)
      .encode("y", yAxisLine)
      .encode("color", lineColor)
      .encode("shape", "smooth")
      .style("lineWidth", 3);

    const initCombined = additionalLinesConfig.flatMap((cfg) => {
      const {
        key,
        xAxis = xAxisLine,
        yAxis = yAxisLine,
        colorKey = "type",
      } = cfg;
      return (additionalLinesDataRef.current[key] ?? []).map((item) => ({
        ...item,
        x: item[xAxis],
        y: item[yAxis],
        type: item[colorKey],
      }));
    });

    const additionalLine = chart
      .line()
      .data(initCombined)
      .encode("x", "x")
      .encode("y", "y")
      .encode("color", "type")
      .encode("shape", "smooth")
      .style("lineWidth", 3);

    chart.render();

    chartRef.current = {
      chart,
      bar,
      line,
      additionalLine,
    };
    // Sync prevRef so the data effect skips a redundant changeData call on next render
    prevAdditionalLinesDataRef.current = additionalLinesDataRef.current;

    return () => {
      chartRef.current?.chart?.destroy();
    };
  }, [
    token,
    barChartDomain,
    barChartRange,
    xAxisLine,
    yAxisLine,
    xAxisBar,
    yAxisBar,
    lineColor,
    barColor,
    additionalLinesConfig, // config changes → full re-render
  ]);

  // Only update chart if data actually changed
  useEffect(() => {
    if (!isEqual(prevDataLineRef.current, dataLine)) {
      chartRef.current.line?.changeData(dataLine);
      prevDataLineRef.current = dataLine;
    }
  }, [dataLine]);

  // Only update chart if data actually changed
  useEffect(() => {
    if (!isEqual(prevDataBarRef.current, dataBar)) {
      chartRef.current.bar?.changeData(dataBar);
      prevDataBarRef.current = dataBar;
    }
  }, [dataBar]);

  // Only update chart if additional lines data actually changed
  useEffect(() => {
    additionalLinesDataRef.current = additionalLinesData;
    if (!isEqual(prevAdditionalLinesDataRef.current, additionalLinesData)) {
      const combined = additionalLinesConfig.flatMap((cfg) => {
        const {
          key,
          xAxis = xAxisLine,
          yAxis = yAxisLine,
          colorKey = "type",
        } = cfg;
        return (additionalLinesData[key] ?? []).map((item) => ({
          ...item,
          x: item[xAxis],
          y: item[yAxis],
          type: item[colorKey],
        }));
      });
      chartRef.current.additionalLine?.changeData(combined);
      prevAdditionalLinesDataRef.current = additionalLinesData;
    }
  }, [additionalLinesData, additionalLinesConfig, xAxisLine, yAxisLine]);

  return <div style={{ width: "100%", height: "100%" }} id={container}></div>;
}
